import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { postJson } from "../api";
import { formatText, useTexts, type Label } from "../texts";
import { ChoiceMenu } from "../ui/ChoiceMenu";
import { ChatIcon } from "../ui/icons/Icon";
import { Field, FormActions, Notice, OptionCards, PageHead, TextField, showResult, useToast } from "../ui/kit";
import { PageState, PasswordField } from "../ui/pieces";
import { errorText, type AiError } from "./ai";
import { useAiData, useTitle } from "./parts";
import { RowsSkeleton } from "./widgets";
import styles from "./ai.module.css";

export type AiOptions = {
  protocols: string[];
  catalogStrategies: string[];
  capabilities: string[];
  providers: { providerId: number; code: string; name: string }[];
};

type Outcome = {
  done: boolean;
  manual?: boolean;
  connectionId?: number;
  providerId?: number | null;
  providerCode?: string | null;
  titleKey: string;
  messageKey: string;
  args?: (string | number)[];
};

/** Nombre de un protocolo para la persona («Compatible con OpenAI (chat)»); uno nuevo, «Desconocido». */
export const protocolName = (L: Label, code: string) => {
  const key = `ADM_AI_PROTOCOL_${code}`;
  const text = L(key);
  return text.startsWith("⟦") ? L("USAGE_UNKNOWN") : text;
};

/**
 * IA › Proveedores › ← Proveedor personalizado (§5.9 `ia-personalizado`): nombre, dirección base, protocolo, clave (C54)
 * y de dónde salen los modelos (C08). Conectado, va a la ficha del proveedor con un toast; si falla, el resultado con
 * «Reintentar» conserva lo escrito.
 */
export function CustomProviderPage() {
  const { L } = useTexts();
  const toast = useToast();
  const navigate = useNavigate();
  const { data, state, retry } = useAiData<AiOptions>("/api/admin/ai/options");
  const [name, setName] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  const [chosenProtocol, setProtocol] = useState("OPENAI_CHAT");
  const [chosenCatalog, setCatalog] = useState("OPENAI_LIST");
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [failure, setFailure] = useState<string | null>(null);
  useTitle(L("ADM_AI_CUSTOM_TITLE"));
  const back = { href: "/Ai/Providers/Index", label: L("ADM_AI_PROVIDERS") };
  const head = <PageHead title={L("ADM_AI_CUSTOM_TITLE")} sub={L("ADM_AI_CUSTOM_SUB")} back={back} />;
  if (state === "failed" || state === "missing")
    return (
      <PageState
        head={head}
        title={L("ADM_UNAVAILABLE")}
        line={L("ADM_UNAVAILABLE_LINE")}
        action={
          <button type="button" className="tm-btn secondary" onClick={retry}>
            {L("ADM_RETRY")}
          </button>
        }
      />
    );
  if (!data)
    return (
      <>
        {head}
        <RowsSkeleton rows={3} />
      </>
    );

  const protocol = data.protocols.includes(chosenProtocol) ? chosenProtocol : (data.protocols[0] ?? chosenProtocol);
  const catalog = data.catalogStrategies.includes(chosenCatalog)
    ? chosenCatalog
    : (data.catalogStrategies[0] ?? chosenCatalog);
  const clear = (field: string, value: string) => {
    if (errors[field] && value.trim())
      setErrors((current) => Object.fromEntries(Object.entries(current).filter(([key]) => key !== field)));
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    const local: Record<string, string> = {};
    if (!name.trim()) local.name = L("AI_PROVIDER_NAME_REQUIRED");
    if (!baseUrl.trim()) local.baseUrl = L("AI_ENDPOINT_REQUIRED");
    if (!key.trim()) local.apiKey = L("CONNECT_NEED_KEY");
    if (Object.keys(local).length > 0) {
      showResult(() => setErrors(local), true);
      return;
    }
    setBusy(true);
    setErrors({});
    const reply = await postJson<Outcome & AiError & { fields?: { field: string; messageKey: string }[] }>(
      "/api/admin/ai/custom",
      { name, baseUrl, protocol, catalog, apiKey: key },
    );
    setBusy(false);
    const body = reply.body;
    if (body?.titleKey) {
      if (body.connectionId) {
        // Guardado (con o sin catálogo): la clave ya no queda en la pantalla y se sigue en la ficha o en Modelo manual.
        setKey("");
        const message = formatText(L(body.messageKey), ...(body.args ?? []));
        if (body.manual) {
          toast.show(formatText(L("ADM_AI_CUSTOM_MANUAL_NEXT"), name.trim()));
          navigate(`/Ai/Avanzado/Manual${body.providerId ? `?providerId=${body.providerId}` : ""}`);
        } else {
          toast.show(
            body.done ? formatText(L("ADM_AI_CUSTOM_CONNECTED"), name.trim(), String(body.args?.[0] ?? 0)) : message,
          );
          navigate(
            body.providerCode ? `/Ai/Providers/${encodeURIComponent(body.providerCode)}` : "/Ai/Providers/Index",
          );
        }
        return;
      }
      setFailure(`${L(body.titleKey)} ${formatText(L(body.messageKey), ...(body.args ?? []))}`);
      requestAnimationFrame(() => document.querySelector<HTMLElement>("main h1")?.focus({ preventScroll: true }));
      return;
    }
    const fields = body?.fields ?? (body?.field ? [{ field: body.field, messageKey: body.messageKey ?? "" }] : []);
    if (fields.length > 0)
      showResult(() => setErrors(Object.fromEntries(fields.map((item) => [item.field, L(item.messageKey)]))), true);
    else showResult(() => setFailure(errorText(L, body)), false);
  };

  // Si falla, el resultado con «Reintentar» vuelve al formulario con lo escrito (como el React de antes).
  if (failure)
    return (
      <>
        {head}
        <Notice
          tone="error"
          action={
            <button type="button" className="tm-btn secondary" onClick={() => setFailure(null)}>
              {L("ADM_RETRY")}
            </button>
          }
        >
          {failure}
        </Notice>
      </>
    );

  return (
    <>
      <PageHead title={L("ADM_AI_CUSTOM_TITLE")} sub={L("ADM_AI_CUSTOM_SUB")} back={back}>
        <span className={styles.desktopOnly}>
          <a className="tm-btn secondary" href="/Ai/Providers/Index">
            {L("ACTION_CANCEL")}
          </a>
          <button
            type="submit"
            form="custom-form"
            className="tm-btn primary"
            disabled={busy}
            aria-busy={busy || undefined}
          >
            {busy ? L("NPA_AI_TESTING") : L("ADM_AI_CONNECT")}
          </button>
        </span>
      </PageHead>
      <form
        id="custom-form"
        className={`tm-form ${styles.formGrid}`}
        noValidate
        onSubmit={(event) => void submit(event)}
      >
        <TextField
          id="custom-name"
          field="name"
          label={L("ADM_AI_NAME")}
          value={name}
          maxLength={100}
          placeholder={L("ADM_AI_CUSTOM_NAME_HINT")}
          error={errors.name}
          onChange={(value) => (setName(value), clear("name", value))}
        />
        <TextField
          id="custom-url"
          field="baseUrl"
          type="url"
          label={L("ADM_AI_BASE_URL")}
          value={baseUrl}
          maxLength={500}
          placeholder={L("ADM_AI_BASE_URL_HINT")}
          error={errors.baseUrl}
          onChange={(value) => (setBaseUrl(value), clear("baseUrl", value))}
        />
        <Field id="custom-protocol" label={L("ADM_AI_PROTOCOL")}>
          <ChoiceMenu
            id="custom-protocol"
            label={L("ADM_AI_PROTOCOL")}
            value={protocol}
            options={data.protocols.map((value) => ({ value, label: protocolName(L, value) }))}
            onChange={setProtocol}
            chevron={<ChatIcon name="chevron-down" />}
            check={<ChatIcon name="check" />}
          />
        </Field>
        <PasswordField
          id="custom-key"
          label={L("ADM_AI_ACCESS_KEY")}
          value={key}
          onChange={(value) => (setKey(value), clear("apiKey", value))}
          autoComplete="new-password"
          error={errors.apiKey}
          help={L("ADM_AI_KEY_STORED")}
        />
        <div className={`tm-field ${styles.full}`}>
          <span id="custom-catalog-label" className={styles.fieldLabel}>
            {L("ADM_AI_MODELS")}
          </span>
          <OptionCards
            labelledBy="custom-catalog-label"
            name="custom-catalog"
            value={catalog}
            options={data.catalogStrategies.map((value) => ({
              value,
              title: L(value === "MANUAL" ? "ADM_AI_CATALOG_MANUAL" : "ADM_AI_CATALOG_LIST"),
              line: L(value === "MANUAL" ? "ADM_AI_CATALOG_MANUAL_LINE" : "ADM_AI_CATALOG_LIST_LINE"),
            }))}
            onChange={setCatalog}
          />
        </div>
        <div className={styles.mobileOnly}>
          <FormActions>
            <a className="tm-btn secondary" href="/Ai/Providers/Index">
              {L("ACTION_CANCEL")}
            </a>
            <button type="submit" className="tm-btn primary" disabled={busy} aria-busy={busy || undefined}>
              {busy ? L("NPA_AI_TESTING") : L("ADM_AI_CONNECT")}
            </button>
          </FormActions>
        </div>
      </form>
    </>
  );
}
